import CookieIcon from "@mui/icons-material/Cookie";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Switch from "@mui/material/Switch";
import { Fragment, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { LeafButton } from "./buttons";

/* =========================
   DEFAULT (GDPR SAFE)
========================= */
const DEFAULT_DATA = {
  feature: true,
  analytics: false,
  advertise: false,
};

/* =========================
   COOKIE HELPERS (SINGLE COOKIE)
========================= */
function setConsent(data) {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);

  const value = encodeURIComponent(JSON.stringify(data));

  document.cookie =
    "cookie_consent_trustedpay=" +
    value +
    "; expires=" +
    expires.toUTCString() +
    "; path=/; SameSite=Lax" +
    (window.location.protocol === "https:" ? "; Secure" : "");
}

function getConsent() {
  const cookies = document.cookie.split("; ");
  const found = cookies.find((c) => c.startsWith("cookie_consent_trustedpay="));
  if (!found) return null;

  try {
    const value = found.substring(found.indexOf("=") + 1);
    const parsed = JSON.parse(decodeURIComponent(value));

    if (
      typeof parsed.feature === "boolean" &&
      typeof parsed.analytics === "boolean" &&
      typeof parsed.advertise === "boolean"
    ) {
      return parsed;
    }

    return null;
  } catch {
    return null;
  }
}

/* =========================
   COMPONENT
========================= */
const AcceptCookie = () => {
  const { t } = useTranslation();

  // INIT ONCE
  const initialConsent = getConsent();

  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useState(initialConsent || DEFAULT_DATA);
  const [hasConsent, setHasConsent] = useState(!!initialConsent);

  /* =========================
     HANDLERS
  ========================= */

  const handleChange = (data) => {
    setPermission((prev) => ({ ...prev, ...data }));
  };

  const handleAccept = () => {
    setConsent(permission);
    setHasConsent(true);
    setOpen(false);
  };

  const handleCancel = () => {
    setPermission(getConsent() || DEFAULT_DATA);
    setOpen(false);
  };

  const handleAcceptAll = () => {
    const allTrue = {
      feature: true,
      analytics: true,
      advertise: true,
    };

    setPermission(allTrue);
    setConsent(allTrue);
    setHasConsent(true);
    setOpen(false);
  };

  const handleReject = () => {
    const allFalse = {
      feature: false,
      analytics: false,
      advertise: false,
    };

    setPermission(allFalse);
    setConsent(allFalse);
    setHasConsent(true);
    setOpen(false);
  };

  /* =========================
     UI (UNCHANGED)
  ========================= */

  return (
    <Fragment>
      {hasConsent ? (
        <button
          className="pre-hidden fixed bottom-10 left-10 w-14 h-14 rounded-full flex items-center justify-center text-white bg-[#565ADD] shadow-header cursor-pointer z-[99]"
          onClick={() => setOpen(true)}
        >
          <CookieIcon />
        </button>
      ) : (
        <div className="pre-hidden bg-white shadow-header sticky inset-x-0 bottom-0 p-[1%] flex flex-col lgmi:flex-row items-start smmi:items-center gap-5 z-[99]">
          <p className="whitespace-pre-wrap flex-1 font-bold">
            <Trans
              i18nKey="content_107"
              components={{ small: <span className="text-sm-2 font-normal" /> }}
            />
          </p>

          <div className="flex items-start justify-end smmi:items-center flex-col smmi:flex-row gap-2">
            <button
              className="whitespace-nowrap p-2.5 text-sm-2 font-semibold text-[--tree-leaf]"
              onClick={() => setOpen(true)}
            >
              {t("content_108")}
            </button>

            <button
              className="whitespace-nowrap p-2.5 text-sm-2 font-semibold text-[--tree-leaf]"
              onClick={handleReject}
            >
              {t("content_109")}
            </button>

            <LeafButton
              title={t("content_110")}
              style={{
                width: "auto",
                fontSize: 15,
                padding: "10px",
              }}
              onClick={handleAcceptAll}
            />
          </div>
        </div>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: {
              xs: "100vw",
              sm: "90vw",
              md: "60vw",
            },
            maxWidth: "none",
            margin: 0,
          },
        }}
      >
        <DialogTitle className="!text-lg !font-bold border-b !p-4">
          {t("content_111")}
        </DialogTitle>

        <DialogContent className="!p-4">
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-10">
              <div>
                <label className="text-base font-bold">
                  {t("content_112")}
                </label>
                <p className="text-sm-2">{t("content_113")}</p>
              </div>
              <p className="whitespace-nowrap font-semibold text-[--tree-leaf]">
                {t("content_120")}
              </p>
            </div>

            <div className="flex items-center gap-10">
              <div>
                <label className="text-base font-bold">
                  {t("content_114")}
                </label>
                <p className="text-sm-2">{t("content_115")}</p>
              </div>
              <Switch
                color="warning"
                checked={permission.feature}
                onChange={(_, v) => handleChange({ feature: v })}
              />
            </div>

            <div className="flex items-center gap-10">
              <div>
                <label className="text-base font-bold">
                  {t("content_116")}
                </label>
                <p className="text-sm-2">{t("content_117")}</p>
              </div>
              <Switch
                color="warning"
                checked={permission.analytics}
                onChange={(_, v) => handleChange({ analytics: v })}
              />
            </div>

            <div className="flex items-center gap-10">
              <div>
                <label className="text-base font-bold">
                  {t("content_118")}
                </label>
                <p className="text-sm-2">{t("content_119")}</p>
              </div>
              <Switch
                color="warning"
                checked={permission.advertise}
                onChange={(_, v) => handleChange({ advertise: v })}
              />
            </div>
          </div>
        </DialogContent>

        <DialogActions className="border-t flex justify-end items-center gap-2 !p-4">
          <button
            className="whitespace-nowrap p-2.5 text-sm-2 font-semibold text-[--tree-leaf]"
            onClick={handleCancel}
          >
            {t("cancel")}
          </button>

          <LeafButton
            title={t("content_121")}
            style={{ width: "auto", padding: "10px", fontSize: 15 }}
            onClick={handleAccept}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default AcceptCookie;
